import React, { useState, useEffect } from "react";
import {
  IconUpload,
  IconFile,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconTrash,
  IconChevronLeft,
  IconChevronRight,
  IconRefresh,
  IconCalendar,
  IconLoader2,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import {
  uploadDocuments,
  fetchDocuments,
  deleteDocument,
  Document as KnowledgeDocument,
  DocumentsResponse,
} from "../../services/documentService";

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadDate: Date;
  status: "pending" | "success" | "error" | "uploading";
  message?: string;
  file: File;
}

// Interface for the confirmation dialog
interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// Confirmation dialog component
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <IconAlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {title}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{message}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${
                isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? (
                <IconLoader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
              ) : null}
              {confirmLabel}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {cancelLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const KnowledgeBasePage: React.FC = () => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [notifications, setNotifications] = useState<
    { message: string; type: "success" | "error" | "info"; id: string }[]
  >([]);
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  // State for document deletion
  const [documentToDelete, setDocumentToDelete] =
    useState<KnowledgeDocument | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const auth = useAuth();

  // Load documents on mount and when refresh is triggered
  useEffect(() => {
    if (auth.isAuthenticated) {
      loadDocuments();
    }
  }, [auth.isAuthenticated]);

  // Function to load documents
  const loadDocuments = async (cursor?: string) => {
    if (!auth.isAuthenticated) {
      showNotification("You must be authenticated to view documents", "error");
      return;
    }

    setIsLoadingDocuments(true);
    setDocumentsError(null);

    try {
      const response = await fetchDocuments(pageSize, cursor);

      setDocuments(response.data);
      setTotalDocuments(response.total_count);
      setNextCursor(response.next_cursor);
      setHasMore(response.has_more);
      setCurrentPage(cursor ? currentPage + 1 : 1);

      if (!cursor) {
        showNotification(`Loaded ${response.data.length} documents`, "info");
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocumentsError(
        error instanceof Error ? error.message : "Failed to load documents",
      );
      showNotification("Failed to load documents. Please try again.", "error");
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  // Function to handle next page
  const handleNextPage = () => {
    if (hasMore && nextCursor) {
      loadDocuments(nextCursor);
    }
  };

  // Function to handle previous page
  const handlePreviousPage = () => {
    // This is more complex since the API doesn't support backward pagination directly
    // For simplicity, we'll just reload the first page
    if (currentPage > 1) {
      loadDocuments();
    }
  };

  // Function to handle document deletion
  const handleDeleteClick = (document: KnowledgeDocument) => {
    setDocumentToDelete(document);
    setIsDeleteDialogOpen(true);
  };

  // Function to confirm document deletion
  const confirmDelete = async () => {
    if (!documentToDelete || !auth.isAuthenticated) {
      return;
    }

    setIsDeleting(true);

    try {
      const result = await deleteDocument(documentToDelete.id);

      if (result.success) {
        showNotification(
          `Document ${documentToDelete.file_name} deleted successfully`,
          "success",
        );
        // Remove the deleted document from state
        setDocuments(documents.filter((doc) => doc.id !== documentToDelete.id));
        // Update total count
        setTotalDocuments((prev) => Math.max(0, prev - 1));
      } else {
        showNotification(
          `Failed to delete document: ${result.message}`,
          "error",
        );
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      showNotification(
        `Error deleting document: ${error instanceof Error ? error.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  // Function to cancel document deletion
  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const showNotification = (
    message: string,
    type: "success" | "error" | "info",
  ) => {
    const id = `notification-${Date.now()}`;
    setNotifications((prev) => [...prev, { message, type, id }]);

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  };

  const handleFiles = (fileList: FileList) => {
    // Convert FileList to Array and filter for supported files
    const filesToProcess = Array.from(fileList).filter((file) => {
      const isDocx = file.name.endsWith(".docx") || file.name.endsWith(".doc");
      const isPdf = file.type.includes("pdf");
      return isDocx || isPdf;
    });

    // Check if any files were filtered out
    if (filesToProcess.length < fileList.length) {
      showNotification(
        "Some files were skipped. Only PDF and DOCX files are supported.",
        "info",
      );
    }

    if (filesToProcess.length === 0) {
      showNotification(
        "No supported files were selected. Please upload PDF or DOCX files.",
        "error",
      );
      return;
    }

    // Check file sizes
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes
    const oversizedFiles = filesToProcess.filter(
      (file) => file.size > MAX_FILE_SIZE,
    );

    if (oversizedFiles.length > 0) {
      showNotification(
        `${oversizedFiles.length} file(s) exceed the 25MB limit and cannot be added`,
        "error",
      );
      // Filter out oversized files
      const validFiles = filesToProcess.filter(
        (file) => file.size <= MAX_FILE_SIZE,
      );
      if (validFiles.length === 0) return;
    }

    // Add files to the list as "pending" (not uploaded yet)
    const newFiles = filesToProcess
      .filter((file) => file.size <= MAX_FILE_SIZE)
      .map((file) => ({
        id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
        name: file.name,
        size: file.size,
        type: file.type,
        uploadDate: new Date(),
        status: "pending" as const,
        file: file, // Store the actual File object to upload later
      }));

    // Add files to state
    setFiles((prev) => [...prev, ...newFiles]);

    showNotification(
      `${newFiles.length} file(s) added. Click "Upload Files" to process.`,
      "info",
    );
  };

  const handleDelete = (id: string) => {
    setFiles(files.filter((file) => file.id !== id));
  };

  const handleUpload = async () => {
    if (!auth.isAuthenticated) {
      showNotification("You must be authenticated to upload files", "error");
      return;
    }

    if (isUploading) {
      showNotification(
        "Please wait until the current upload is complete",
        "info",
      );
      return;
    }

    // Get only pending files that haven't been uploaded yet
    const pendingFiles = files.filter((f) => f.status === "pending");

    if (pendingFiles.length === 0) {
      showNotification("No files to upload", "info");
      return;
    }

    setIsUploading(true);

    // Update status to uploading
    setFiles((prev) =>
      prev.map((f) =>
        pendingFiles.some((pf) => pf.id === f.id)
          ? { ...f, status: "uploading" }
          : f,
      ),
    );

    try {
      // Extract the actual File objects
      const filesToUpload = pendingFiles.map((f) => f.file);

      // Upload all files in a single batch
      const response = await uploadDocuments(filesToUpload);

      if (response.success) {
        // Update all files to success
        setFiles((prev) =>
          prev.map((f) =>
            pendingFiles.some((pf) => pf.id === f.id)
              ? { ...f, status: "success", message: "Uploaded successfully" }
              : f,
          ),
        );
        showNotification(
          `${pendingFiles.length} file(s) uploaded successfully`,
          "success",
        );

        // Refresh document list
        loadDocuments();
      } else {
        // Update all files to error
        setFiles((prev) =>
          prev.map((f) =>
            pendingFiles.some((pf) => pf.id === f.id)
              ? { ...f, status: "error", message: response.message }
              : f,
          ),
        );
        showNotification(`Upload failed: ${response.message}`, "error");
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      // Update all files to error
      setFiles((prev) =>
        prev.map((f) =>
          pendingFiles.some((pf) => pf.id === f.id)
            ? {
                ...f,
                status: "error",
                message:
                  error instanceof Error ? error.message : "Unknown error",
              }
            : f,
        ),
      );
      showNotification("Error uploading files. Please try again.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + " MB";
    else return (bytes / 1073741824).toFixed(1) + " GB";
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <IconCheck className="h-5 w-5 text-green-500" />;
      case "error":
        return <IconAlertCircle className="h-5 w-5 text-red-500" />;
      case "uploading":
        return (
          <div className="h-5 w-5 border-2 border-t-brand-primary rounded-full animate-spin"></div>
        );
      case "pending":
      default:
        return null;
    }
  };

  // Count files by status
  const pendingFiles = files.filter((f) => f.status === "pending").length;
  const uploadedFiles = files.filter((f) => f.status === "success").length;
  const failedFiles = files.filter((f) => f.status === "error").length;

  // Format document creation date
  const formatDate = (dateString: string): string => {
    try {
      // Always convert to timestamp and multiply by 1000 for consistency
      const timestamp = new Date(dateString).getTime();
      if (!isNaN(timestamp)) {
        // If valid date, multiply by 1000
        const date = new Date(timestamp * 1000);
        return formatDateObject(date);
      }

      // Fallback: try direct parsing as ISO string
      return formatDateObject(new Date(dateString));
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString; // Return original string if parsing fails
    }
  };

  // Helper function to format a Date object consistently
  const formatDateObject = (date: Date): string => {
    return (
      date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }) +
      ", " +
      date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    );
  };

  // Get file icon based on filename
  const getFileIcon = (filename: string | undefined) => {
    if (!filename) {
      return <IconFile className="h-5 w-5 text-gray-400 mr-2" />;
    }

    if (filename.toLowerCase().endsWith(".pdf")) {
      return <IconFile className="h-5 w-5 text-red-400 mr-2" />;
    } else if (
      filename.toLowerCase().endsWith(".docx") ||
      filename.toLowerCase().endsWith(".doc")
    ) {
      return <IconFile className="h-5 w-5 text-blue-400 mr-2" />;
    } else {
      return <IconFile className="h-5 w-5 text-gray-400 mr-2" />;
    }
  };

  // Calculate pagination information
  const calculatePaginationInfo = () => {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(start + documents.length - 1, totalDocuments);
    return `${start} to ${end} of ${totalDocuments}`;
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 relative">
      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        title="Delete Document"
        message={`Are you sure you want to delete document "${documentToDelete?.file_name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isLoading={isDeleting}
      />

      {/* Notifications */}
      <div className="absolute top-4 right-4 z-50">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`mb-2 p-3 rounded-md shadow-md flex items-center justify-between ${
              notification.type === "success"
                ? "bg-green-50 text-green-800 border-green-200"
                : notification.type === "error"
                  ? "bg-red-50 text-red-800 border-red-200"
                  : "bg-blue-50 text-blue-800 border-blue-200"
            } border`}
          >
            <div className="flex items-center">
              {notification.type === "success" ? (
                <IconCheck className="mr-2 h-5 w-5" />
              ) : notification.type === "error" ? (
                <IconAlertCircle className="mr-2 h-5 w-5" />
              ) : (
                <IconFile className="mr-2 h-5 w-5" />
              )}
              <span>{notification.message}</span>
            </div>
            <button
              onClick={() =>
                setNotifications((prev) =>
                  prev.filter((n) => n.id !== notification.id),
                )
              }
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              <IconX size={18} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex-1 p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">
            Knowledge Base
          </h1>
          <p className="text-gray-500 mt-2">
            Upload documents to enhance the chatbot's knowledge base
          </p>
        </div>

        {/* Upload area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 mb-8 text-center transition-colors ${
            isDragging
              ? "border-brand-primary bg-brand-primary/5"
              : "border-gray-300 hover:border-brand-primary/50"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="mx-auto flex flex-col items-center">
            <IconUpload
              size={40}
              className={`${isDragging ? "text-brand-primary" : "text-gray-400"} mb-4`}
            />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isDragging ? "Drop files here" : "Drag and drop files here"}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              or click to browse from your computer
            </p>
            <label
              className={`cursor-pointer bg-brand-primary hover:bg-brand-primary/90 text-white py-2 px-4 rounded-md transition-colors ${isUploading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Browse Files
              <input
                type="file"
                multiple
                accept=".pdf,.docx,.doc"
                className="hidden"
                onChange={handleFileInputChange}
                disabled={isUploading}
              />
            </label>
            <p className="text-xs text-gray-500 mt-4">
              Supported formats: PDF, DOCX (max 25MB per file)
            </p>
          </div>
        </div>

        {/* Upload Button and Stats */}
        {pendingFiles > 0 && (
          <div className="mb-4 flex flex-wrap items-center justify-between bg-white p-4 shadow-sm rounded-lg">
            <div className="flex items-center">
              <span className="font-medium">
                Ready to upload: {pendingFiles} file(s)
              </span>
              {uploadedFiles > 0 && (
                <span className="ml-4 text-green-600">
                  <IconCheck className="inline h-4 w-4 mr-1" />
                  {uploadedFiles} uploaded
                </span>
              )}
              {failedFiles > 0 && (
                <span className="ml-4 text-red-600">
                  <IconAlertCircle className="inline h-4 w-4 mr-1" />
                  {failedFiles} failed
                </span>
              )}
            </div>
            <button
              onClick={handleUpload}
              disabled={isUploading || pendingFiles === 0}
              className={`bg-brand-primary text-white px-4 py-2 rounded-md flex items-center ${
                isUploading || pendingFiles === 0
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-brand-primary/90"
              }`}
            >
              <IconUpload size={18} className="mr-2" />
              {isUploading ? "Uploading..." : "Upload Files"}
            </button>
          </div>
        )}

        {/* File list - Pending/Uploaded/Failed files */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Upload Queue</h2>
          </div>

          {files.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <p className="text-gray-500">No files added yet</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {files.map((file) => (
                <li
                  key={file.id}
                  className="px-6 py-4 flex items-center justify-between"
                >
                  <div className="flex items-center flex-1 min-w-0">
                    <IconFile className="h-6 w-6 text-gray-400 mr-3 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {file.name}
                      </p>
                      <div className="flex text-sm text-gray-500 space-x-4">
                        <span>{formatFileSize(file.size)}</span>
                        <span>{file.uploadDate.toLocaleDateString()}</span>
                        <span
                          className={`${
                            file.status === "pending"
                              ? "text-yellow-600"
                              : file.status === "success"
                                ? "text-green-600"
                                : file.status === "error"
                                  ? "text-red-600"
                                  : "text-blue-600"
                          } font-medium`}
                        >
                          {file.status === "pending"
                            ? "Pending"
                            : file.status === "success"
                              ? "Uploaded"
                              : file.status === "error"
                                ? "Failed"
                                : "Uploading"}
                        </span>
                      </div>
                      {file.message && (
                        <p
                          className={`text-xs mt-1 ${
                            file.status === "error"
                              ? "text-red-500"
                              : "text-gray-500"
                          }`}
                        >
                          {file.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="mr-2">{getStatusIcon(file.status)}</div>
                    {/* Only show delete button for pending files */}
                    {file.status === "pending" && (
                      <button
                        onClick={() => handleDelete(file.id)}
                        className="text-gray-400 hover:text-brand-danger transition-colors p-2 rounded-full hover:bg-gray-100"
                        disabled={isUploading}
                      >
                        <IconTrash size={18} />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Knowledge Base Documents List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">
              Knowledge Base Documents
            </h2>

            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                <span className="font-medium">{totalDocuments}</span> documents
              </div>

              <button
                onClick={() => loadDocuments()}
                className="flex items-center text-brand-primary hover:text-brand-primary/80"
                disabled={isLoadingDocuments}
              >
                {isLoadingDocuments ? (
                  <IconLoader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <IconRefresh className="h-5 w-5 mr-2" />
                )}
                Refresh
              </button>
            </div>
          </div>

          {/* Documents table */}
          {documentsError ? (
            <div className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <IconAlertCircle className="h-8 w-8 text-red-500" />
              </div>
              <p className="text-red-600 font-medium">
                Error loading documents
              </p>
              <p className="text-gray-500 mt-1">{documentsError}</p>
              <button
                onClick={() => loadDocuments()}
                className="mt-4 px-4 py-2 bg-brand-primary text-white rounded-md"
              >
                Try Again
              </button>
            </div>
          ) : isLoadingDocuments && documents.length === 0 ? (
            <div className="p-10 flex justify-center">
              <div className="flex flex-col items-center">
                <div className="h-10 w-10 border-4 border-t-brand-primary rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500">Loading documents...</p>
              </div>
            </div>
          ) : documents.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <p className="text-gray-500">
                No documents found in the knowledge base
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Upload documents using the form above
              </p>
            </div>
          ) : (
            <>
              <div className="w-full">
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-5/12">
                        Filename
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-3/12 hidden sm:table-cell">
                        Date Added
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/12">
                        Status
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 hidden md:table-cell">
                        Purpose
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {documents.map((doc) => (
                      <tr key={doc.id} className="hover:bg-gray-50">
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getFileIcon(doc.file_name)}
                            <span className="font-medium text-gray-900 truncate max-w-full">
                              {doc.file_name}
                              {/* Show date on mobile */}
                              <span className="block text-xs text-gray-500 mt-1 sm:hidden">
                                {formatDate(doc.created_at)}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                          <div className="flex items-center">
                            <IconCalendar className="h-4 w-4 mr-1 text-gray-400" />
                            <span className="truncate">
                              {formatDate(doc.created_at)}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`px-2 py-1 text-xs inline-flex items-center justify-center rounded-full ${
                              doc.status === "completed"
                                ? "bg-green-50 text-green-700"
                                : doc.status === "pending"
                                  ? "bg-yellow-50 text-yellow-700"
                                  : doc.status === "failed"
                                    ? "bg-red-50 text-red-700"
                                    : "bg-blue-50 text-blue-700"
                            }`}
                          >
                            {doc.status}
                          </span>
                          {/* Show purpose on mobile */}
                          <span className="block text-xs text-gray-500 mt-1 md:hidden">
                            {doc.purpose}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                          <span className="truncate block">{doc.purpose}</span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          <button
                            onClick={() => handleDeleteClick(doc)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-2 rounded-full hover:bg-gray-100"
                            title="Delete document"
                            aria-label="Delete document"
                          >
                            <IconTrash size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls */}
              <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-sm text-gray-500 text-center sm:text-left">
                  Showing{" "}
                  <span className="font-medium">
                    {calculatePaginationInfo()}
                  </span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1 || isLoadingDocuments}
                    className={`flex items-center px-3 py-1 rounded border ${
                      currentPage === 1 || isLoadingDocuments
                        ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <IconChevronLeft size={16} className="mr-1" />
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={!hasMore || isLoadingDocuments}
                    className={`flex items-center px-3 py-1 rounded border ${
                      !hasMore || isLoadingDocuments
                        ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    Next
                    <IconChevronRight size={16} className="ml-1" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBasePage;
